import { ReactNode, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  AppBar, Box, Drawer, IconButton, List, ListItemButton, ListItemIcon,
  ListItemText, Toolbar, Typography, Avatar, Menu, MenuItem, Divider,
  alpha, Tooltip, Badge, useMediaQuery, useTheme, Chip, Snackbar, Alert,
} from '@mui/material';
import {
  Menu as MenuIcon, Notifications, DarkMode, LightMode, Logout, ChevronLeft, ChevronRight, Language,
  Person, VerifiedUser,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout, selectUser, selectIsSuperAdmin, selectPermissions, selectRoleSlugs } from '../../store/slices/authSlice';
import { toggleTheme } from '../../store/slices/themeSlice';
import { selectEnabledModules, selectEnabledFeatures } from '../../store/slices/tenantSlice';
import { useTenantBootstrap } from '../../hooks/useTenantBootstrap';
import { resolveServiceIcon } from '../../constants/serviceIcons';
import { filterAdminNavSections, findNavLabel, resolveNavSections } from '../../config/adminNavigation';
import { getRoleLabel, isPartnerPortalUserType } from '../../config/roleNavigation';
import { hasAnyPermissionMatch } from '../../utils/permissions';
import { useI18n } from '../../i18n/I18nProvider';
import { navI18nKey } from '../../i18n/navLabels';
import type { RootState } from '../../store';
import api from '../../services/api';
import { useAdminNotifications } from '../../hooks/useAdminNotifications';
import TenantSwitcher from './TenantSwitcher';
import NotificationPanel from './NotificationPanel';
import ZoneScopeBar from './ZoneScopeBar';
import BackendStatusBanner from './BackendStatusBanner';
import IdleLogoutGuard from './IdleLogoutGuard';
import DemoTenantBanner from '../demo/DemoTenantBanner';
import { useBackendHealth } from '../../hooks/useBackendHealth';
import { resolveAssetUrl } from '../../utils/assetUrl';

const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 72;
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

interface DashboardLayoutProps {
  children: ReactNode;
}

function loadSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(loadSidebarCollapsed);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const userPermissions = useSelector(selectPermissions, shallowEqual);
  const roleSlugs = useSelector(selectRoleSlugs, shallowEqual);
  const enabledModules = useSelector(selectEnabledModules, shallowEqual);
  const enabledFeatures = useSelector(selectEnabledFeatures, shallowEqual);
  const tenant = useSelector((state: RootState) => state.tenant);
  const { language, setLanguage, langList, t, dir } = useI18n();
  const isRtl = dir === 'rtl';
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const tr = useCallback((label: string, sectionId?: string) => t(navI18nKey(label, sectionId), label), [t]);

  const drawerWidth = isMobile ? DRAWER_WIDTH_EXPANDED : (collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED);
  const isCollapsedView = !isMobile && collapsed;
  const tooltipSide = isRtl ? 'left' : 'right';
  const menuHorizontal = isRtl ? 'left' : 'right';

  useTenantBootstrap();

  const tenantLogoUrl = resolveAssetUrl(tenant.branding?.logo || '') || undefined;
  const tenantFaviconUrl = resolveAssetUrl(tenant.branding?.favicon || '');
  useEffect(() => {
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    if (!link.dataset.defaultHref) link.dataset.defaultHref = link.getAttribute('href') || '/favicon.ico';
    link.href = tenantFaviconUrl || link.dataset.defaultHref;
  }, [tenantFaviconUrl]);

  const [pushToast, setPushToast] = useState<{ title: string; body: string } | null>(null);

  const { registerWebPushToken } = useAdminNotifications((payload) => {
    if (payload.popup === false) return;
    const title = payload.title?.trim() || 'Notification';
    const body = payload.body?.trim() || '';
    setPushToast({ title, body });
  });

  useEffect(() => {
    if (user?.id) {
      // Silent: only works if browser already granted Notification permission.
      registerWebPushToken(false).catch(() => {});
    }
  }, [user?.id, registerWebPushToken]);

  const [notifAnchor, setNotifAnchor] = useState<HTMLElement | null>(null);

  const openNotifications = useCallback(async (el: HTMLElement) => {
    setNotifAnchor(el);
    await registerWebPushToken(true).catch(() => false);
  }, [registerWebPushToken]);

  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    }
  }, [collapsed, isMobile]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const hasAnyPermission = (requiredPermissions?: string[]) => hasAnyPermissionMatch(userPermissions, requiredPermissions);

  const navSections = useMemo(
    () => filterAdminNavSections(resolveNavSections(user?.userType, roleSlugs), {
      isSuperAdmin,
      userType: user?.userType,
      enabledModules,
      enabledFeatures,
      hasAnyPermission,
    }),
    [isSuperAdmin, enabledModules, enabledFeatures, userPermissions, user?.userType, roleSlugs]
  );

  const pageTitleRaw = findNavLabel(location.pathname, navSections) || 'Dashboard';
  const pageTitle = tr(pageTitleRaw);
  const isPartnerPortal = isPartnerPortalUserType(user?.userType);
  const accountLabel = tr('My Account', 'account');
  const partnerProfileLabel = tr('Profile & Documents', 'partner_profile');
  const roleLabel = getRoleLabel(user?.userType);
  const logoutColor = theme.palette.error.main;

  const handleLogout = () => {
    setAnchorEl(null);
    dispatch(logout());
    navigate('/login');
  };
  const isDispatcherView = location.pathname.startsWith('/dispatcher');
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isSubServiceWorkspace = pathSegments[0] === 'hubs' && pathSegments.length >= 3;
  const shouldAutoCollapseSidebar = isDispatcherView || isSubServiceWorkspace;
  const prevCollapsedRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (isMobile) return;
    if (shouldAutoCollapseSidebar) {
      if (prevCollapsedRef.current === null) {
        prevCollapsedRef.current = collapsed;
      }
      if (!collapsed) setCollapsed(true);
    } else if (prevCollapsedRef.current !== null) {
      setCollapsed(prevCollapsedRef.current);
      prevCollapsedRef.current = null;
    }
  }, [shouldAutoCollapseSidebar, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps
  const { isBackendUp } = useBackendHealth();

  const { data: unreadData } = useQuery({
    queryKey: ['notification-unread-count'],
    queryFn: async () => {
      const res = await api.get('/notifications/unread-count');
      return res.data.data as { unread: number };
    },
    enabled: isBackendUp,
    refetchInterval: isBackendUp ? 60_000 : false,
    retry: false,
  });

  const isActive = (path: string) => {
    const [basePath, query] = path.split('?');
    // Paths with a query string are action deep-links, not a lasting route highlight
    if (query) return false;
    if (basePath === '/dashboard') return location.pathname === '/dashboard';

    const pathname = location.pathname;
    const matches = (candidate: string) =>
      pathname === candidate || pathname.startsWith(`${candidate}/`);

    if (!matches(basePath)) return false;

    // Prefer the most specific nav item so /tenants does not stay selected on
    // /tenants/subscription-plans (and the same for other nested routes).
    const allPaths = navSections.flatMap((section) =>
      section.items.map((item) => item.path.split('?')[0]),
    );
    const matching = allPaths.filter(matches);
    if (!matching.length) return true;
    const best = matching.reduce((a, b) => (a.length >= b.length ? a : b));
    return best === basePath;
  };

  const drawerTransition = theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

  const isLight = theme.palette.mode === 'light';
  const sidebarInk = isLight ? '#0C1222' : '#080C16';
  const sidebarText = isLight ? '#F4F6FB' : '#E8EDF5';
  const sidebarMuted = isLight ? '#94A3B8' : '#8B97AB';
  const sidebarLine = isLight ? alpha('#FFFFFF', 0.08) : alpha('#E8EEF8', 0.08);

  const drawerPaperSx = {
    width: drawerWidth,
    boxSizing: 'border-box' as const,
    borderInlineEnd: `1px solid ${sidebarLine}`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    transition: drawerTransition,
    overflowX: 'hidden' as const,
    bgcolor: sidebarInk,
    color: sidebarText,
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        bgcolor: sidebarInk,
        color: sidebarText,
      }}
    >
      <Box
        sx={{
          px: isCollapsedView ? 1 : 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: `1px solid ${sidebarLine}`,
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 100%)`,
          justifyContent: isCollapsedView ? 'center' : 'flex-start',
          position: 'relative',
          minHeight: 72,
        }}
      >
        <Tooltip title={tenant.companyName || 'Platform'} placement="right" disableHoverListener={!isCollapsedView}>
          <Avatar
            src={tenantLogoUrl}
            imgProps={{ style: { objectFit: 'contain' } }}
            sx={{
              bgcolor: tenantLogoUrl ? '#ffffff' : theme.palette.primary.main,
              width: isCollapsedView ? 38 : 42,
              height: isCollapsedView ? 38 : 42,
              fontWeight: 800,
              fontSize: '1rem',
              letterSpacing: '-0.02em',
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
              flexShrink: 0,
              border: `2px solid ${alpha('#ffffff', 0.3)}`,
            }}
          >
            {tenant.companyName?.[0] || 'S'}
          </Avatar>
        </Tooltip>
        {!isCollapsedView && (
          <Box minWidth={0} flex={1}>
            <Typography variant="subtitle2" fontWeight={700} noWrap lineHeight={1.2} sx={{ letterSpacing: '-0.02em', color: sidebarText }}>
              {isSuperAdmin ? 'Super SaaS' : tenant.companyName || 'Platform'}
            </Typography>
            <Chip
              size="small"
              label={getRoleLabel(user?.userType, roleSlugs)}
              sx={{
                mt: 0.6,
                height: 18,
                fontSize: '0.62rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                bgcolor: alpha(isSuperAdmin ? theme.palette.secondary.main : theme.palette.primary.main, 0.22),
                color: '#fff',
                borderRadius: '6px',
              }}
            />
          </Box>
        )}
        {!isMobile && (
          <IconButton
            size="small"
            onClick={toggleCollapsed}
            sx={{
              position: isCollapsedView ? 'absolute' : 'static',
              insetInlineEnd: isCollapsedView ? 4 : undefined,
              top: isCollapsedView ? 4 : undefined,
              bgcolor: alpha('#FFFFFF', 0.08),
              color: sidebarText,
              border: `1px solid ${sidebarLine}`,
              '&:hover': { bgcolor: alpha('#FFFFFF', 0.14) },
            }}
            aria-label={collapsed ? t('admin.label.expand_sidebar', 'Expand sidebar') : t('admin.label.collapse_sidebar', 'Collapse sidebar')}
          >
            {collapsed
              ? (isRtl ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />)
              : (isRtl ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />)}
          </IconButton>
        )}
      </Box>

      <List
        sx={{
          flex: 1,
          minHeight: 0,
          px: isCollapsedView ? 0.5 : 1.25,
          py: 1.5,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha('#0B1220', 0.85)} ${alpha('#000', 0.28)}`,
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': {
            background: alpha('#000', 0.35),
            borderRadius: 999,
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#0B1220', 0.92),
            borderRadius: 999,
            border: `2px solid ${alpha('#1e293b', 0.9)}`,
            '&:hover': { background: '#020617' },
          },
        }}
      >
        {navSections.map((section) => (
          <Box key={section.id} mb={0.25}>
            {!isCollapsedView ? (
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  px: 1.5,
                  pt: section.id === 'overview' ? 0.25 : 1.25,
                  pb: 0.5,
                  fontWeight: 800,
                  fontSize: '0.62rem',
                  letterSpacing: '0.1em',
                  lineHeight: 1.4,
                  color: section.accent ? alpha(section.accent, 0.92) : sidebarMuted,
                }}
              >
                {tr(section.label, section.id)}
              </Typography>
            ) : section.id !== 'overview' ? (
              <Divider sx={{ my: 1.25, mx: 0.75, opacity: 0.5 }} />
            ) : null}
            {section.items.map((item) => {
              const active = isActive(item.path);
              const accent = section.accent || theme.palette.primary.main;
              const itemLabel = tr(item.label);
              const button = (
                <ListItemButton
                  key={`${item.path}-${item.label}`}
                  selected={active}
                  onClick={() => { navigate(item.path); setMobileOpen(false); }}
                  sx={{
                    borderRadius: 2,
                    mb: 0.25,
                    py: 0.85,
                    mx: isCollapsedView ? 0.25 : 0.5,
                    px: isCollapsedView ? 1 : undefined,
                    ps: isCollapsedView ? undefined : 1.5,
                    pe: isCollapsedView ? undefined : 1,
                    justifyContent: isCollapsedView ? 'center' : 'flex-start',
                    minHeight: 44,
                    transition: 'all 0.15s ease',
                    color: active ? '#fff' : sidebarMuted,
                    ...(active && {
                      bgcolor: alpha(accent, 0.22),
                      boxShadow: isCollapsedView
                        ? 'none'
                        : `inset ${isRtl ? '-3px' : '3px'} 0 0 ${accent}`,
                      '& .MuiListItemIcon-root': { color: '#fff' },
                    }),
                    '&:hover': { bgcolor: alpha('#FFFFFF', active ? 0.1 : 0.06) },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isCollapsedView ? 0 : 34,
                      justifyContent: 'center',
                      color: active ? '#fff' : sidebarMuted,
                    }}
                  >
                    {resolveServiceIcon(item.icon)}
                  </ListItemIcon>
                  {!isCollapsedView && (
                    <ListItemText
                      primary={itemLabel}
                      primaryTypographyProps={{
                        fontWeight: active ? 700 : 500,
                        fontSize: '0.8125rem',
                        noWrap: true,
                        color: active ? '#fff' : sidebarText,
                      }}
                    />
                  )}
                </ListItemButton>
              );

              return isCollapsedView ? (
                <Tooltip key={`${item.path}-${item.label}`} title={itemLabel} placement={tooltipSide} arrow>
                  {button}
                </Tooltip>
              ) : button;
            })}
          </Box>
        ))}
      </List>

      <Box sx={{
        p: isCollapsedView ? 1 : 1.5,
        borderTop: `1px solid ${sidebarLine}`,
        bgcolor: alpha('#000', 0.18),
      }}>
        <Tooltip
          title={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Account'}
          placement={tooltipSide}
          disableHoverListener={!isCollapsedView}
        >
          <Box
            sx={{
              p: isCollapsedView ? 0.75 : 1,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsedView ? 'center' : 'flex-start',
              gap: 1,
              border: `1px solid ${sidebarLine}`,
              bgcolor: alpha('#FFFFFF', 0.05),
              cursor: 'pointer',
              transition: 'background-color 0.14s ease',
              '&:hover': { bgcolor: alpha('#FFFFFF', 0.09) },
            }}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <Avatar sx={{
              width: 30,
              height: 30,
              bgcolor: theme.palette.primary.main,
              fontSize: '0.8rem',
              fontWeight: 700,
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
            }}>
              {user?.firstName?.[0]}
            </Avatar>
            {!isCollapsedView && (
              <Box minWidth={0} flex={1}>
                <Typography variant="caption" fontWeight={700} noWrap display="block" sx={{ fontSize: '0.78rem', color: sidebarText }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="caption" noWrap display="block" fontSize="0.68rem" sx={{ color: sidebarMuted }}>
                  {user?.email || user?.phone}
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>
        <Tooltip title={t('admin.label.logout', 'Log out')} placement={tooltipSide} disableHoverListener={!isCollapsedView}>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              mt: 0.75,
              py: 0.85,
              mx: isCollapsedView ? 0.25 : 0,
              px: isCollapsedView ? 1 : 1.25,
              justifyContent: isCollapsedView ? 'center' : 'flex-start',
              minHeight: 40,
              color: logoutColor,
              '&:hover': { bgcolor: alpha(logoutColor, 0.12) },
            }}
          >
            <ListItemIcon sx={{ minWidth: isCollapsedView ? 0 : 32, justifyContent: 'center', color: logoutColor }}>
              <Logout fontSize="small" />
            </ListItemIcon>
            {!isCollapsedView && (
              <ListItemText
                primary={t('admin.label.logout', 'Log out')}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.8125rem', noWrap: true, color: logoutColor }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          marginInlineStart: { md: `${drawerWidth}px` },
          bgcolor: alpha(theme.palette.background.paper, 0.78),
          backdropFilter: 'blur(18px) saturate(1.35)',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: { xs: isDispatcherView ? 48 : 58, sm: isDispatcherView ? 52 : 66 }, gap: 0.5 }}>
          <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ me: 1, display: { md: 'none' }, borderRadius: '10px' }}>
            {mobileOpen ? (isRtl ? <ChevronRight /> : <ChevronLeft />) : <MenuIcon />}
          </IconButton>
          <Box flexGrow={1} minWidth={0}>
            {!isDispatcherView && (
              <>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2} noWrap sx={{ fontSize: '1.05rem', letterSpacing: '-0.025em' }}>{pageTitle}</Typography>
                {!isMobile && !collapsed && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
                    {isSuperAdmin
                      ? `Platform admin · ${tenant.companyName || localStorage.getItem('tenantSlug') || 'demo'}`
                      : `${getRoleLabel(user?.userType, roleSlugs)} · ${tenant.companyName || 'Tenant'}`}
                  </Typography>
                )}
              </>
            )}
            {isDispatcherView && !isMobile && (
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                Operations workspace · real-time dispatch
              </Typography>
            )}
            {isSubServiceWorkspace && !isDispatcherView && !isMobile && (
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                Service workspace
              </Typography>
            )}
          </Box>
          {!isDispatcherView && !isSubServiceWorkspace && <Box sx={{ me: 1.5, display: { xs: 'none', lg: 'block' } }}><TenantSwitcher /></Box>}
          <Tooltip title={t('common.label.language', 'Language')}>
            <IconButton onClick={(e) => setLangAnchor(e.currentTarget)} size="small" sx={{ borderRadius: '10px' }}>
              <Language fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={langAnchor} open={!!langAnchor} onClose={() => setLangAnchor(null)}>
            {langList.map((lang) => (
              <MenuItem key={lang.code} selected={language === lang.code} onClick={() => { setLanguage(lang.code); setLangAnchor(null); }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{lang.flag}</span>
                  <Box>
                    <Typography variant="body2" fontWeight={language === lang.code ? 700 : 400}>{lang.nativeLabel}</Typography>
                    <Typography variant="caption" color="text.secondary">{lang.label}</Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Menu>
          <Tooltip title={t('admin.label.toggle_theme', 'Toggle theme')}>
            <IconButton onClick={() => dispatch(toggleTheme())} size="small" sx={{ borderRadius: '10px' }}>
              {theme.palette.mode === 'light' ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('admin.nav.notifications', 'Notifications')} disableHoverListener={Boolean(notifAnchor)}>
            <IconButton
              onClick={(e) => { void openNotifications(e.currentTarget); }}
              size="small"
              sx={{ borderRadius: '10px' }}
              aria-label="Notifications"
            >
              <Badge badgeContent={unreadData?.unread || 0} color="error">
                <Notifications fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
          <NotificationPanel anchorEl={notifAnchor} onClose={() => setNotifAnchor(null)} />
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              ms: 0.5,
              p: 0.5,
              borderRadius: '12px',
              border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              '&:hover': {
                border: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
                bgcolor: 'transparent',
              },
            }}
          >
            <Avatar sx={{
              width: 30,
              height: 30,
              bgcolor: theme.palette.primary.main,
              fontSize: '0.8rem',
              fontWeight: 700,
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
            }}>
              {user?.firstName?.[0]}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: menuHorizontal, vertical: 'top' }}
            anchorOrigin={{ horizontal: menuHorizontal, vertical: 'bottom' }}
            slotProps={{ paper: { sx: { minWidth: 220, mt: 0.75 } } }}
          >
            <MenuItem disabled sx={{ opacity: '1 !important', flexDirection: 'column', alignItems: 'flex-start', py: 1.25 }}>
              <Typography variant="body2" fontWeight={700}>{user?.firstName} {user?.lastName}</Typography>
              <Typography variant="caption" color="text.secondary">{user?.email || user?.phone}</Typography>
              {roleLabel && (
                <Chip label={roleLabel} size="small" sx={{ mt: 0.75, height: 22, fontSize: '0.68rem', fontWeight: 700 }} />
              )}
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/account'); }}>
              <ListItemIcon><Person fontSize="small" /></ListItemIcon>
              {accountLabel}
            </MenuItem>
            {isPartnerPortal && (
              <MenuItem onClick={() => { setAnchorEl(null); navigate('/my-profile'); }}>
                <ListItemIcon><VerifiedUser fontSize="small" /></ListItemIcon>
                {partnerProfileLabel}
              </MenuItem>
            )}
            <Divider />
            <MenuItem
              onClick={handleLogout}
              sx={{
                color: logoutColor,
                fontWeight: 600,
                '&:hover': { bgcolor: alpha(logoutColor, 0.08) },
              }}
            >
              <ListItemIcon sx={{ color: logoutColor }}><Logout fontSize="small" /></ListItemIcon>
              {t('admin.label.logout', 'Log out')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          transition: drawerTransition,
        }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          anchor={isRtl ? 'right' : 'left'}
          sx={{ '& .MuiDrawer-paper': drawerPaperSx }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: isDispatcherView ? 0 : { xs: 2, sm: 2.5, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: isDispatcherView ? '48px' : '58px', sm: isDispatcherView ? '52px' : '66px' },
          minHeight: { xs: isDispatcherView ? 'calc(100dvh - 48px)' : 'calc(100dvh - 58px)', sm: isDispatcherView ? 'calc(100dvh - 52px)' : 'calc(100dvh - 66px)' },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <BackendStatusBanner />
        <IdleLogoutGuard />
        <DemoTenantBanner />
        {!isDispatcherView && <ZoneScopeBar />}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden' }}>
          {children}
        </Box>
      </Box>

      <Snackbar
        open={!!pushToast}
        autoHideDuration={6000}
        onClose={() => setPushToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setPushToast(null)} severity="info" variant="filled" sx={{ width: '100%' }}>
          <Typography variant="subtitle2" fontWeight={700}>{pushToast?.title}</Typography>
          {pushToast?.body ? (
            <Typography variant="body2">{pushToast.body}</Typography>
          ) : null}
        </Alert>
      </Snackbar>
    </Box>
  );
}
